import React, { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosInstance";
import { useDispatch, useSelector } from "react-redux";
import {
  setConvenienceAmount,
  setHandlingAmount,
} from "../../store/slices/IdSlice";
import { TooltipLink } from "../TooltipLink/TooltipLink";
import { Link } from "react-router-dom";
import CdnSvg from "../codeSvg/CdnSvg";

interface PaymentsProps {
  proceedToPay: boolean;
}

interface PaymentOption {
  Id: string;
  Label: string;
  IconUrl: string;
  HandlingFeeType: "percentage" | "fixed";
  HandlingFeeAmount: string;
  HandlingFeeCurrency: string;
  ConvenienceFee?: string;
  HandlingFeeNote: string;
  Tooltip: string;
  isDisabled: boolean;
  IsActive: boolean;
  LeftSvg: string;
}

const Payments: React.FC<PaymentsProps> = ({ proceedToPay }) => {
  const dispatch = useDispatch();
  const token = sessionStorage.getItem("tk");
  const billingRecordID = sessionStorage.getItem("billingRecordID");
  const selectedCoupon = sessionStorage.getItem("selectedCoupon");
  const discountAmount = sessionStorage.getItem("discountAmount");
  const tipValue = sessionStorage.getItem("tipValue");
  const uuid = localStorage.getItem("uuid");

  const { cartValuePrice: totalAmount, totalPayableAmt: totalPayableAmount } =
    useSelector((store: any) => store.ID);

  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [paymentOptions, setPaymentOptions] = useState<PaymentOption[]>([]);

  const getFeeAmount = (amount: number, type: string): string => {
    if (isNaN(amount)) return "0.00";
    return type === "percentage"
      ? (totalAmount * (amount / 100)).toFixed(2)
      : amount.toFixed(2);
  };

  const getAllFinalAmount = () => {
    const selected = paymentOptions.find((opt) => opt.Id === selectedOption);
    if (!selected) return;

    const handlingAmt = getFeeAmount(
      Number(selected.HandlingFeeAmount),
      selected.HandlingFeeType
    );
    const convenienceAmt = selected.ConvenienceFee || "0.00";

    dispatch(setHandlingAmount(handlingAmt));
    dispatch(setConvenienceAmount(convenienceAmt));
  };

  useEffect(() => {
    getPaymentOptions();
  }, []);

  useEffect(() => {
    if (!selectedOption) return;
    getAllFinalAmount();
  }, [selectedOption, paymentOptions, totalAmount, discountAmount, tipValue]);

  const getPaymentOptions = async () => {
    try {
      const response = await axiosInstance.get("/api/openRoute/paymentOptions");
      const options = response.data?.paymentOptions || [];
      setPaymentOptions(options);
      if (options.length) {
        setSelectedOption(options[0].Id);
        getAllFinalAmount();
      }
    } catch (error) {
    }
  };

  const handleSelect = (optionId: string) => {
    setSelectedOption(optionId);
  };

  const handleProceedToPay = () => {
    if (token && uuid) {
      const fetchOrderData = async () => {
        try {
          const postData = {
            UUID: uuid,
            PaymentId: selectedOption,
            TipAmount: tipValue,
            AddressId: billingRecordID,
            DiscountId: selectedCoupon,
          };

          const response = await axiosInstance.post(
            "/api/user/createOrder",
            postData
          );
          if (response.data?.href) {
            window.location.href = response.data?.href;
          }
        } catch (error) {
          console.error("cart :: Error fetching order data:", error);
        }
      };
      fetchOrderData();
    }
  };
  if (!paymentOptions.length) return null;
  return (
    <div className="d-flex flex-column align-items-center col-md-6 mx-auto gap-3 col-11">
      {/* Payment Summary */}
      <div className="w-100 ff-medium text-center bg-beige text-noti p-2 px-2 my-2 d-flex align-items-center justify-content-center gap-2 rounded-3 font-14">
        Total amount to pay
        <span className="ff-bold">
          {" "}
          <span className="ff-currency ff-medium font-16"></span>
          {totalPayableAmount}
        </span>
      </div>

      <hr className="w-100 m-0 opacity-25 text-black-50" />

      {/* Payment Selection */}
      <div className="w-100">
        <p className="mb-3 font-14 ff-semibold text-center text-black">
          Choose your payment method
        </p>

        {paymentOptions.map((option: PaymentOption) => (
          <div
            key={option.Id}
            className={`d-flex align-items-center rounded-3 layout-border gap-3 p-3 my-2 payment-option ${
              selectedOption === option.Id ? "Pay-active" : ""
            } ${option.isDisabled ? "disabled" : ""}`}
            style={{ cursor: "pointer" }}
            onClick={() => handleSelect(option.Id)}
          >
            <div className="payType">
              <CdnSvg url={option.LeftSvg} />
            </div>

            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-2 font-14 ff-semibold">
                <img src={option.IconUrl} alt={option.Label} height="22" />
                <div>{option.Label}</div>
              </div>

              <div className="d-flex align-items-center gap-2 mt-1 payTool-tip">
                <div className="ff-medium font-13 text-black-50">
                  {option.HandlingFeeNote}{" "}
                  <span className="ff-currency ff-medium font-16"></span>
                  {getFeeAmount(
                    Number(option.HandlingFeeAmount),
                    option.HandlingFeeType
                  )}
                </div>
                <TooltipLink
                  Id={`tooltip-${option.Id}`}
                  placement="bottom"
                  className="tooltip-black"
                  title={
                    <div
                      style={{
                        backgroundColor: "#000",
                        color: "#fff",
                        padding: "6px 10px",
                        borderRadius: "4px",
                        fontSize: "12px",
                      }}
                    >
                      {option.Tooltip}
                    </div>
                  }
                >
                  <svg
                    className="info-icon"
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path
                      d="M6 0C9.3138 0 12 2.68514 12 5.99763C12.0012 7.57229 11.3829 9.08427 10.2785 10.2071C9.17415 11.33 7.67229 11.9737 6.09721 11.9992C4.52214 12.0247 3.0002 11.43 1.86 10.3435C0.719794 9.25699 0.0527931 7.76582 0.00300007 6.19195L0 5.99763L0.00239997 5.82969C0.0912 2.59517 2.742 0 6 0ZM6 5.39786H5.4L5.3298 5.40206C5.18397 5.4194 5.04957 5.4896 4.95205 5.59936C4.85453 5.70913 4.80067 5.85083 4.80067 5.99763C4.80067 6.14443 4.85453 6.28613 4.95205 6.39589C5.04957 6.50565 5.18397 6.57585 5.3298 6.59319L5.4 6.59739V8.39668L5.4042 8.46685C5.41999 8.60073 5.48045 8.72538 5.57581 8.8207C5.67117 8.91603 5.79587 8.97645 5.9298 8.99224L6 8.99644H6.6L6.6702 8.99224C6.80413 8.97645 6.92883 8.91603 7.02419 8.8207C7.11955 8.72538 7.18 8.60073 7.1958 8.46685L7.2 8.39668L7.1958 8.32651C7.18146 8.20414 7.12976 8.08916 7.04774 7.9972C6.96573 7.90523 6.85737 7.84075 6.7374 7.81251L6.6702 7.80051L6.6 7.79692V5.99763L6.5958 5.92746C6.58 5.79358 6.51955 5.66892 6.42419 5.5736C6.32883 5.47828 6.20413 5.41785 6.0702 5.40206L6 5.39786ZM6.006 3.59858L5.9298 3.60277C5.78397 3.62011 5.64957 3.69031 5.55205 3.80008C5.45453 3.90984 5.40067 4.05154 5.40067 4.19834C5.40067 4.34514 5.45453 4.48684 5.55205 4.5966C5.64957 4.70636 5.78397 4.77656 5.9298 4.7939L6 4.7981L6.0762 4.7939C6.22203 4.77656 6.35643 4.70636 6.45395 4.5966C6.55147 4.48684 6.60533 4.34514 6.60533 4.19834C6.60533 4.05154 6.55147 3.90984 6.45395 3.80008C6.35643 3.69031 6.22203 3.62011 6.0762 3.60277L6.006 3.59858Z"
                      fill="#BEBEBE"
                    />
                  </svg>
                </TooltipLink>
              </div>
            </div>

            <div>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <circle
                  cx="11"
                  cy="11"
                  r="11"
                  fill={selectedOption === option.Id ? "#6E42E5" : "#EAEAEA"}
                />

                <path
                  d="M7 11l2 2 5-5"
                  stroke="#fff"
                  strokeWidth="2"
                  fill="none"
                />
              </svg>
            </div>
          </div>
        ))}
        <div className="mt-3">
          <p className="font-13 ff-medium text-center">
            By proceeding, you agree to our{" "}
            <Link
              className="ff-bold text-black"
              to="/termsandconditions"
              target="_blank"
            >
              T&C
            </Link>
            ,{" "}
            <Link
              className="ff-bold text-black"
              to="/privacyandpolicy"
              target="_blank"
            >
              Privacy
            </Link>{" "}
            and{" "}
            <Link
              className="ff-bold text-black"
              to="/CancellationPolicy"
              target="_blank"
            >
              Cancellation Policy.
            </Link>
          </p>
        </div>
        <div className="d-flex align-items-center justify-content-center mt-3">
          <button
            className="w-50 font-14 ff-semibold blue-button px-4 rounded-3"
            disabled={!proceedToPay}
            onClick={handleProceedToPay}
          >
            Proceed to Pay
          </button>
        </div>
      </div>
    </div>
  );
};

export default Payments;
